import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Memory Grid — vue React staircase + debrief (cyanotype Wave M2-game-chrome).
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DebriefShell, useGameShellFromContext } from '@labrain/game-shell';
import { staircaseConfig, variantForLevel } from './params';
function cellEq(a, b) {
    return a.row === b.row && a.col === b.col;
}
function cellKey(c) {
    return `${c.row}-${c.col}`;
}
function Grid({ size, litCells, tappedCells, errorCells, successCells, onTap, disabled, }) {
    const { t } = useTranslation('games');
    const cells = [];
    for (let r = 0; r < size; r += 1) {
        for (let c = 0; c < size; c += 1)
            cells.push({ row: r, col: c });
    }
    const litSet = new Set(litCells.map(cellKey));
    const tappedSet = new Set(tappedCells.map(cellKey));
    const errorSet = new Set(errorCells.map(cellKey));
    const successSet = new Set(successCells.map(cellKey));
    return (_jsx("div", { className: "grid w-full max-w-[340px] gap-s2 border border-cyan-border-faint bg-cyan-surface p-s4", style: {
            gridTemplateColumns: `repeat(${size}, minmax(0, 1fr))`,
            aspectRatio: '1 / 1',
        }, children: cells.map((cell) => {
            const k = cellKey(cell);
            const isLit = litSet.has(k);
            const isTapped = tappedSet.has(k);
            const isError = errorSet.has(k);
            const isSuccess = successSet.has(k);
            const stateClass = isError
                ? 'bg-cyan-media text-cyan-surface'
                : isSuccess
                    ? 'bg-cyan-core text-cyan-surface'
                    : isLit
                        ? 'bg-cyan-network text-cyan-surface scale-95'
                        : isTapped
                            ? 'bg-cyan-network/15 text-cyan-text border border-cyan-network/30'
                            : 'bg-cyan-bg text-cyan-text-secondary hover:bg-cyan-border-faint';
            return (_jsx("button", { type: "button", onClick: () => onTap && !disabled && onTap(cell), disabled: disabled, "aria-label": t('memory_grid.cell_aria', { row: cell.row + 1, col: cell.col + 1 }), className: [
                    'flex aspect-square items-center justify-center',
                    'transition-all duration-feedback ease-soft-wind',
                    stateClass,
                    disabled ? 'cursor-not-allowed' : 'cursor-pointer active:scale-95',
                ].join(' '), children: isTapped &&
                    tappedCells.findIndex((tt) => cellEq(tt, cell)) > -1 && (_jsx("span", { className: "font-mono text-[14px]", children: tappedCells.findIndex((tt) => cellEq(tt, cell)) + 1 })) }, k));
        }) }));
}
export function MGStaircaseView({ adapter, level, onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const subscribe = useCallback((notify) => adapter.subscribe(() => notify()), [adapter]);
    const getSnapshot = useCallback(() => adapter.getState(), [adapter]);
    const adapterState = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    const shell = useGameShellFromContext();
    const variant = variantForLevel(level);
    const handleTap = useCallback((cell) => adapter.handleTap(cell), [adapter]);
    const litCells = adapterState.subPhase === 'presenting' &&
        adapterState.sequence &&
        adapterState.presentingIdx >= 0 &&
        adapterState.presentingIdx < adapterState.sequence.cells.length
        ? [adapterState.sequence.cells[adapterState.presentingIdx]]
        : [];
    const errorCells = adapterState.subPhase === 'feedback' && adapterState.feedback === 'incorrect'
        ? adapterState.sequence?.expected ?? []
        : [];
    const successCells = adapterState.subPhase === 'feedback' && adapterState.feedback === 'correct'
        ? adapterState.sequence?.expected ?? []
        : [];
    const trialDisplay = shell.snapshot.staircase.trialIdx + 1;
    const sequenceArrow = variant.mode === 'backward' ? '↩︎' : '→';
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s5 py-s4", children: [_jsxs("div", { className: "flex w-full items-center justify-between text-[12px] text-cyan-text-secondary", children: [_jsx("span", { className: "font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: t('common:labels.trial_count', {
                            current: Math.min(trialDisplay, staircaseConfig.maxTrials),
                            total: staircaseConfig.maxTrials,
                        }) }), _jsx("span", { className: "font-mono text-[11px]", children: t('memory_grid.sequence_label', {
                            count: adapterState.sequence?.length ?? 0,
                            arrow: sequenceArrow,
                        }) }), _jsx("button", { type: "button", onClick: () => {
                            shell.abort('user');
                            onAbort();
                        }, className: "border border-cyan-border-faint px-s3 py-s1 text-[11px] text-cyan-text-secondary hover:border-cyan-border", children: t('common:actions.stop') })] }), _jsxs("div", { className: "flex h-[36px] items-center justify-center text-[14px]", children: [adapterState.subPhase === 'pre_sequence' && (_jsx("span", { className: "text-cyan-text-secondary", children: t('memory_grid.phases.watching') })), adapterState.subPhase === 'presenting' && (_jsx("span", { className: "font-display text-cyan-network", children: t('memory_grid.phases.encoding') })), adapterState.subPhase === 'go_signal' && (_jsx("span", { className: "font-display text-[20px] font-medium text-cyan-text", children: t('memory_grid.phases.go_signal') })), adapterState.subPhase === 'awaiting_input' && (_jsx("span", { className: "text-cyan-text-secondary", children: variant.mode === 'backward'
                            ? t('memory_grid.phases.input_backward')
                            : t('memory_grid.phases.input_forward') })), adapterState.subPhase === 'feedback' && (_jsx("span", { className: [
                            'font-display text-[18px] font-medium',
                            adapterState.feedback === 'correct'
                                ? 'text-cyan-core'
                                : 'text-cyan-media',
                        ].join(' '), children: adapterState.feedback === 'correct'
                            ? `✓ ${t('memory_grid.feedback.correct')}`
                            : `✗ ${t('memory_grid.feedback.incorrect')}` }))] }), _jsx(Grid, { size: variant.gridSize, litCells: litCells, tappedCells: adapterState.subPhase === 'awaiting_input' ||
                    adapterState.subPhase === 'feedback'
                    ? adapterState.taps
                    : [], errorCells: errorCells, successCells: successCells, onTap: handleTap, disabled: adapterState.subPhase !== 'awaiting_input' }), _jsx("div", { className: "text-[11px] text-cyan-text-secondary", children: variant.mode === 'forward'
                    ? t('memory_grid.footer_forward')
                    : t('memory_grid.footer_backward') })] }));
}
function spanComparisonKey(span) {
    if (span === null)
        return null;
    if (span <= 3)
        return 'memory_grid.debrief.equiv_below_avg';
    if (span <= 5)
        return 'memory_grid.debrief.equiv_avg';
    if (span <= 7)
        return 'memory_grid.debrief.equiv_above';
    return 'memory_grid.debrief.equiv_high';
}
function recommendKey(span) {
    if (span !== null && span >= 6) {
        return 'memory_grid.debrief.recommend_excellent';
    }
    if (span !== null && span >= 4) {
        return 'memory_grid.debrief.recommend_good';
    }
    return 'memory_grid.debrief.recommend_keep';
}
export function MGDebriefView({ level: _level, onAbort: _onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const shell = useGameShellFromContext();
    const staircase = shell.snapshot.staircase;
    const span = staircase.thresholdEstimate;
    const trialsCompleted = staircase.trialIdx;
    const trialsCorrect = staircase.trialLog.filter((tt) => tt.response.correct).length;
    const accuracy = trialsCompleted === 0
        ? 0
        : Math.round((trialsCorrect / trialsCompleted) * 100);
    const bestSpan = useMemo(() => {
        let best = 0;
        for (const tt of staircase.trialLog) {
            if (tt.response.correct && tt.value > best)
                best = tt.value;
        }
        return Math.round(best);
    }, [staircase]);
    const fallback = !!staircase.fallback;
    const spanLabel = span === null ? '—' : span.toFixed(1).replace(/\.0$/, '');
    const replay = () => {
        window.location.reload();
    };
    const equivKeyValue = spanComparisonKey(span);
    const equivLabel = equivKeyValue === null ? '—' : t(equivKeyValue);
    return (_jsx(DebriefShell, { eyebrow: t('common:eyebrow.session_done'), title: t('memory_grid.debrief.title'), scoreLabel: t('memory_grid.debrief.score_label'), scoreValue: spanLabel, equivalentAge: equivLabel, fallbackBadge: fallback, metrics: [
            { label: t('common:metrics.trials'), value: trialsCompleted },
            { label: t('common:metrics.correct_pct'), value: `${accuracy}%` },
            { label: t('memory_grid.debrief.metric_longest'), value: bestSpan },
        ], recommendation: t(recommendKey(span)), scientificCaveat: t('memory_grid.debrief.scientific_caveat'), ctaReplay: _jsx("button", { type: "button", onClick: replay, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('common:actions.replay') }), ctaBack: _jsx(Link, { to: "/library", className: "inline-flex h-12 w-full items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: t('common:actions.choose_another') }) }));
}
//# sourceMappingURL=staircaseView.js.map